/**
 * Performance page state that must survive leaving the page.
 *
 * The period, the tab and the person open in the side panel used to live in component
 * state, so opening an OKR and pressing Back reset all three to their defaults. They now
 * live in the URL, which is the only state the browser restores on Back and on reload.
 *
 * Pure functions only, so the rules are testable without rendering the page.
 */

import type { PeriodType } from '../types';

export type PerformanceTabId = 'my-performance' | 'team-performance' | 'organization';

export interface PerformanceUrlState {
  tab?: PerformanceTabId;
  periodType: PeriodType;
  /** Both set, or both undefined — a half-specified range is ignored. */
  startDate?: string;
  endDate?: string;
  /** User whose detail panel is open on the Team tab. */
  userId?: string;
}

const PERIOD_TYPES: readonly PeriodType[] = ['monthly', 'quarterly', 'half_yearly', 'yearly'];
const TAB_IDS: readonly PerformanceTabId[] = ['my-performance', 'team-performance', 'organization'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const PERFORMANCE_URL_KEYS = {
  tab: 'tab',
  periodType: 'period',
  startDate: 'start',
  endDate: 'end',
  userId: 'user',
} as const;

const isValidDate = (value: string | null): value is string =>
  !!value && !Number.isNaN(Date.parse(value));

/**
 * Read state from the URL. Anything malformed is dropped rather than trusted: a hand-edited
 * or stale link must fall back to defaults, never reach the API as garbage.
 */
export function parsePerformanceUrlState(params: URLSearchParams): PerformanceUrlState {
  const rawPeriod = params.get(PERFORMANCE_URL_KEYS.periodType);
  const periodType = PERIOD_TYPES.includes(rawPeriod as PeriodType)
    ? (rawPeriod as PeriodType)
    : 'quarterly';

  const rawTab = params.get(PERFORMANCE_URL_KEYS.tab);
  const tab = TAB_IDS.includes(rawTab as PerformanceTabId) ? (rawTab as PerformanceTabId) : undefined;

  const start = params.get(PERFORMANCE_URL_KEYS.startDate);
  const end = params.get(PERFORMANCE_URL_KEYS.endDate);
  const hasRange = isValidDate(start) && isValidDate(end);

  const rawUser = params.get(PERFORMANCE_URL_KEYS.userId);
  const userId = rawUser && UUID_RE.test(rawUser) ? rawUser : undefined;

  return {
    tab,
    periodType,
    startDate: hasRange ? start : undefined,
    endDate: hasRange ? end : undefined,
    userId,
  };
}

/**
 * Apply a partial update to the URL params. `undefined` removes a key. Keys this page does
 * not own are left alone.
 */
export function applyPerformanceUrlState(
  params: URLSearchParams,
  patch: Partial<PerformanceUrlState>
): URLSearchParams {
  const next = new URLSearchParams(params);
  (Object.keys(patch) as Array<keyof PerformanceUrlState>).forEach((key) => {
    const value = patch[key];
    const urlKey = PERFORMANCE_URL_KEYS[key];
    if (value === undefined || value === '') {
      next.delete(urlKey);
    } else {
      next.set(urlKey, value);
    }
  });
  return next;
}
